<template>
  <div class="w-full p-4">
    <div ref="topStickyRef" class="sticky top-14 z-30 bg-gray-200 -mx-4 px-4 pt-3 pb-1">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">Client Expenses</h2>
      <div class="text-xs text-gray-600">
        Every client-reimbursable record across all time. Read-only.
      </div>
    </div>
    <!-- Filters -->
    <div class="flex flex-wrap gap-3 mb-3 items-end text-sm">
      <div class="flex-1 min-w-[14rem]">
        <label class="block text-xs text-gray-500 mb-0.5">Search</label>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Search description, submitter, client, engagement..."
          class="w-full h-9 px-2 border border-gray-400 rounded-md"
        />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Submitter</label>
        <select v-model="filters.submitter_id" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="u in submitterOptions" :key="u.id" :value="u.id">{{ fullName(u) }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Client</label>
        <select v-model="filters.client_name" class="h-9 px-2 border border-gray-400 rounded-md w-44">
          <option :value="null">All</option>
          <option v-for="name in clientOptions" :key="name" :value="name">{{ name }}</option>
        </select>
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">Engagement</label>
        <select v-model="filters.engagement_title" class="h-9 px-2 border border-gray-400 rounded-md w-56">
          <option :value="null">All</option>
          <option v-for="title in engagementOptions" :key="title" :value="title">{{ title }}</option>
        </select>
      </div>
      <div class="min-w-[16rem]">
        <label class="block text-xs text-gray-500 mb-0.5 flex items-center gap-2">
          <span>Tags</span>
          <span v-if="filters.tags.length > 1" class="ml-auto inline-flex rounded border border-gray-300 overflow-hidden text-[10px]">
            <button type="button" :class="['px-1.5 py-0.5', filters.tagCombinator === 'or' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.tagCombinator = 'or'">OR</button>
            <button type="button" :class="['px-1.5 py-0.5 border-l border-gray-300', filters.tagCombinator === 'and' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.tagCombinator = 'and'">AND</button>
          </span>
        </label>
        <TagPicker v-model="filters.tags" placeholder="Any" />
      </div>
      <div class="min-w-[16rem]">
        <label class="block text-xs text-gray-500 mb-0.5 flex items-center gap-2">
          <span>Exclude tags</span>
          <span v-if="filters.exclude_tags.length > 1" class="ml-auto inline-flex rounded border border-gray-300 overflow-hidden text-[10px]">
            <button type="button" :class="['px-1.5 py-0.5', filters.excludeTagCombinator === 'or' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.excludeTagCombinator = 'or'">OR</button>
            <button type="button" :class="['px-1.5 py-0.5 border-l border-gray-300', filters.excludeTagCombinator === 'and' ? 'bg-accent-primary text-white' : 'bg-white']" @click="filters.excludeTagCombinator = 'and'">AND</button>
          </span>
        </label>
        <TagPicker v-model="filters.exclude_tags" placeholder="None" />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">From</label>
        <input v-model="filters.date_from" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">To</label>
        <input v-model="filters.date_to" type="date" class="h-9 px-2 border border-gray-400 rounded-md w-36" />
      </div>
      <div v-if="canViewIsRetro">
        <label class="block text-xs text-gray-500 mb-0.5">Retro</label>
        <select v-model="filters.is_retro" class="h-9 px-2 border border-gray-400 rounded-md w-32">
          <option :value="null">All</option>
          <option :value="true">Retro only</option>
          <option :value="false">Non-retro</option>
        </select>
      </div>
      <button
        v-if="hasActiveFilters"
        class="px-2 py-1.5 text-xs border border-gray-400 rounded hover:bg-gray-100"
        @click="clearFilters"
      >Clear filters</button>
    </div>

    <!-- Grouping selector + filtered total -->
    <div class="flex items-center mb-3 text-sm">
      <div class="flex items-center gap-2">
        <label class="text-xs text-gray-500">Grouping</label>
        <select v-model="grouping" class="h-9 px-2 border border-gray-400 rounded-md w-48 text-sm">
          <option value="ungrouped">Ungrouped</option>
          <option value="client">Grouped by Client</option>
          <option value="engagement">Grouped by Engagement</option>
          <option value="invoice">Grouped by Invoice</option>
        </select>
      </div>
      <div class="ml-auto text-gray-600">
        Filtered total
        <span class="font-semibold text-dark-primary">${{ formatTotal(filteredTotal) }}</span>
        <span class="text-gray-400 mx-1">·</span>
        {{ visibleExpenses.length }} row<span v-if="visibleExpenses.length !== 1">s</span>
      </div>
    </div>
    </div>

    <!-- Table -->
    <div class="border border-gray-400 rounded-md">
      <table class="table-auto w-full text-sm">
        <thead class="bg-dark-primary text-white sticky z-20" :style="{ top: (topStickyHeight + 56) + 'px' }">
          <tr>
            <th class="text-left px-3 py-2 w-16">ID</th>
            <th class="text-left px-3 py-2">Submitter</th>
            <th class="text-left px-3 py-2">Date</th>
            <th class="text-left px-3 py-2">Category</th>
            <th class="text-left px-3 py-2">Description</th>
            <th class="text-right px-3 py-2">Amount</th>
            <th class="text-left px-3 py-2">Status</th>
            <th class="text-left px-3 py-2">Client / Engagement</th>
            <th class="text-left px-3 py-2">Invoiced</th>
            <th class="text-left px-3 py-2">Client Paid</th>
            <th class="text-center px-3 py-2">Receipts</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="11" class="px-3 py-4 text-center text-gray-500">Loading...</td>
          </tr>
          <tr v-else-if="visibleExpenses.length === 0">
            <td colspan="11" class="px-3 py-4 text-center text-gray-500">No client expenses match these filters.</td>
          </tr>
          <template v-for="row in displayRows" :key="row.kind === 'group' ? `g-${row.key}` : row.exp.id">
            <tr
              v-if="row.kind === 'group'"
              class="bg-amber-50 border-t border-amber-200 cursor-pointer hover:bg-amber-100"
              @click="toggleGroupCollapsed(row.key)"
            >
              <td colspan="11" class="px-3 py-2 text-sm">
                <span class="text-gray-500 mr-2 text-xs">{{ isGroupCollapsed(row.key) ? '▶' : '▼' }}</span>
                <span class="font-semibold text-dark-primary">{{ row.headerLabel }}</span>
                <span class="ml-3 text-xs text-gray-600">
                  {{ row.count }} record<span v-if="row.count !== 1">s</span>,
                  total ${{ formatAmount(row.total) }}
                </span>
              </td>
            </tr>
            <tr v-else-if="!row.groupKey || !isGroupCollapsed(row.groupKey)" class="border-t border-gray-200 hover:bg-gray-50">
              <td class="px-3 py-2 whitespace-nowrap text-gray-500">
                <router-link :to="`/expenses/${row.exp.id}`" class="text-accent-primary hover:underline">#{{ row.exp.id }}</router-link>
              </td>
              <td class="px-3 py-2 whitespace-nowrap">{{ fullName(row.exp.submitter) }}</td>
              <td class="px-3 py-2 whitespace-nowrap">{{ formatDate(row.exp.date_of_expense) }}</td>
              <td class="px-3 py-2 whitespace-nowrap">{{ row.exp.category ? row.exp.category.name : '—' }}</td>
              <td class="px-3 py-2">
                {{ row.exp.description }}
                <div v-if="row.exp.tags && row.exp.tags.length" class="mt-1 flex flex-wrap gap-1">
                  <span
                    v-for="tag in row.exp.tags"
                    :key="tag.id"
                    @click.stop="addTagToFilter(tag)"
                    title="Click to filter by this tag"
                    class="inline-block px-1.5 py-0.5 text-xs rounded bg-accent-primary text-white cursor-pointer hover:bg-accent-secondary"
                  >{{ tag.name }}</span>
                </div>
              </td>
              <td class="px-3 py-2 text-right whitespace-nowrap">
                <span
                  v-if="row.exp.payment_method === 'company_paid'"
                  class="mr-1 px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700"
                  title="Paid via company card"
                >co.</span>
                ${{ formatAmount(row.exp.amount) }}
              </td>
              <td class="px-3 py-2">
                <StatusPill :status="row.exp.status" />
                <div v-if="row.exp.is_retro" class="mt-1">
                  <span class="inline-block px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-500">Retro</span>
                </div>
              </td>
              <td class="px-3 py-2">
                <span>{{ row.exp.client_name || '—' }}</span>
                <span v-if="row.exp.engagement_title"> — {{ row.exp.engagement_title }}</span>
              </td>
              <td class="px-3 py-2">
                <span v-if="row.exp.client_invoiced" class="text-green-700">
                  Yes<span v-if="row.exp.client_invoice_batch_reference"> ({{ row.exp.client_invoice_batch_reference }})</span>
                </span>
                <span v-else class="text-gray-400">No</span>
              </td>
              <td class="px-3 py-2">
                <span v-if="row.exp.client_paid" class="text-green-700">Yes</span>
                <span v-else class="text-gray-400">No</span>
              </td>
              <td class="px-3 py-2 text-center">{{ row.exp.receipt_count || 0 }}</td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default { name: "client-expenses" };
</script>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import { getExpenses } from "@/services/Expense.js";
import StatusPill from "./StatusPill.vue";
import TagPicker from "./TagPicker.vue";
import { useStickyOffset } from "@/composables/useStickyOffset";

const store = useStore();
const $toast = useToast();

const canViewIsRetro = computed(() => {
  const p = store.state.auth.userPermissions || {};
  return !!(p.isDev || p.isSuperuser || p.isExpenseAdmin || p.isAccountingGroup);
});

const loading = ref(false);
const expenses = ref([]);
const grouping = ref('ungrouped');
const topStickyRef = ref(null);
const topStickyHeight = useStickyOffset(topStickyRef);
const collapsedGroupKeys = ref(new Set());

function toggleGroupCollapsed(key) {
  const next = new Set(collapsedGroupKeys.value);
  if (next.has(key)) next.delete(key); else next.add(key);
  collapsedGroupKeys.value = next;
}

function isGroupCollapsed(key) {
  return collapsedGroupKeys.value.has(key);
}

const filters = reactive({
  search: "",
  submitter_id: null,
  client_name: null,
  engagement_title: null,
  group_label: "",
  tags: [],
  tagCombinator: "or",
  exclude_tags: [],
  excludeTagCombinator: "or",
  is_retro: null,
  date_from: null,
  date_to: null,
});

const submitterOptions = computed(() => {
  const map = new Map();
  for (const e of expenses.value) {
    if (e.submitter && !map.has(e.submitter.id)) map.set(e.submitter.id, e.submitter);
  }
  return Array.from(map.values()).sort((a, b) => {
    const an = (`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.username || '').toLowerCase();
    const bn = (`${b.first_name || ''} ${b.last_name || ''}`.trim() || b.username || '').toLowerCase();
    return an.localeCompare(bn);
  });
});

const clientOptions = computed(() => {
  const set = new Set();
  for (const e of expenses.value) if (e.client_name) set.add(e.client_name);
  return Array.from(set).sort();
});

const engagementOptions = computed(() => {
  const set = new Set();
  for (const e of expenses.value) if (e.engagement_title) set.add(e.engagement_title);
  return Array.from(set).sort();
});

const hasActiveFilters = computed(() => !!(
  filters.search ||
  filters.submitter_id ||
  filters.client_name ||
  filters.engagement_title ||
  filters.group_label ||
  (filters.tags && filters.tags.length) ||
  (filters.exclude_tags && filters.exclude_tags.length) ||
  filters.is_retro !== null ||
  filters.date_from ||
  filters.date_to
));

function addTagToFilter(tag) {
  if (!tag) return;
  const idMatch = tag.id != null && filters.tags.some((t) => t.id === tag.id);
  const nameMatch = filters.tags.some((t) => (t.name || '').toLowerCase() === (tag.name || '').toLowerCase());
  if (idMatch || nameMatch) return;
  filters.tags.push({ id: tag.id, name: tag.name });
}

function clearFilters() {
  filters.search = "";
  filters.submitter_id = null;
  filters.client_name = null;
  filters.engagement_title = null;
  filters.group_label = "";
  filters.tags = [];
  filters.tagCombinator = "or";
  filters.exclude_tags = [];
  filters.excludeTagCombinator = "or";
  filters.is_retro = null;
  filters.date_from = null;
  filters.date_to = null;
}

function passesFilters(e) {
  if (filters.search) {
    const q = filters.search.trim().toLowerCase();
    if (q) {
      const submitterText = e.submitter
        ? `${e.submitter.first_name || ''} ${e.submitter.last_name || ''} ${e.submitter.username || ''}`
        : '';
      const haystack = [
        e.description,
        e.category ? e.category.name : '',
        e.client_name,
        e.engagement_title,
        submitterText,
        e.group_label,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
  }
  if (filters.submitter_id && (!e.submitter || e.submitter.id !== filters.submitter_id)) return false;
  if (filters.client_name && e.client_name !== filters.client_name) return false;
  if (filters.engagement_title && e.engagement_title !== filters.engagement_title) return false;
  if (filters.group_label) {
    const wanted = filters.group_label.trim().toLowerCase();
    if (wanted && (e.group_label || '').toLowerCase() !== wanted) return false;
  }
  if (filters.tags && filters.tags.length) {
    const wantedIds = new Set(filters.tags.map((t) => t.id).filter((x) => x != null));
    const wantedNames = new Set(filters.tags.map((t) => (t.name || '').toLowerCase()).filter(Boolean));
    const has = (tag) => wantedIds.has(tag.id) || wantedNames.has((tag.name || '').toLowerCase());
    const expTags = e.tags || [];
    if (filters.tagCombinator === 'and') {
      const expIds = new Set(expTags.map((t) => t.id));
      const expNames = new Set(expTags.map((t) => (t.name || '').toLowerCase()));
      const allMatch = filters.tags.every((t) => (t.id != null && expIds.has(t.id)) || expNames.has((t.name || '').toLowerCase()));
      if (!allMatch) return false;
    } else {
      if (!expTags.some(has)) return false;
    }
  }
  if (filters.exclude_tags && filters.exclude_tags.length) {
    const excludeIds = new Set(filters.exclude_tags.map((t) => t.id).filter((x) => x != null));
    const excludeNames = new Set(filters.exclude_tags.map((t) => (t.name || '').toLowerCase()).filter(Boolean));
    const expTags = e.tags || [];
    const has = (tag) => excludeIds.has(tag.id) || excludeNames.has((tag.name || '').toLowerCase());
    if (filters.excludeTagCombinator === 'and') {
      // Hide only rows that carry ALL of the excluded tags.
      const expIds = new Set(expTags.map((t) => t.id));
      const expNames = new Set(expTags.map((t) => (t.name || '').toLowerCase()));
      const hasAll = filters.exclude_tags.every((t) => (t.id != null && expIds.has(t.id)) || expNames.has((t.name || '').toLowerCase()));
      if (hasAll) return false;
    } else if (expTags.some(has)) {
      return false;
    }
  }
  if (filters.is_retro !== null && !!e.is_retro !== filters.is_retro) return false;
  if (filters.date_from && (!e.date_of_expense || e.date_of_expense < filters.date_from)) return false;
  if (filters.date_to && (!e.date_of_expense || e.date_of_expense > filters.date_to)) return false;
  return true;
}

const visibleExpenses = computed(() => {
  const list = expenses.value.filter(passesFilters);
  return [...list].sort((a, b) => {
    if (grouping.value === 'invoice') {
      const ar = a.client_invoice_batch_reference;
      const br = b.client_invoice_batch_reference;
      if (ar !== br) {
        if (!ar) return 1;
        if (!br) return -1;
        return ar < br ? -1 : 1;
      }
    } else if (grouping.value === 'engagement') {
      const ac = (a.client_name || '').toLowerCase();
      const bc = (b.client_name || '').toLowerCase();
      if (ac !== bc) return ac < bc ? -1 : 1;
      const ae = (a.engagement_title || '').toLowerCase();
      const be = (b.engagement_title || '').toLowerCase();
      if (ae !== be) return ae < be ? -1 : 1;
    } else {
      const ac = (a.client_name || '').toLowerCase();
      const bc = (b.client_name || '').toLowerCase();
      if (ac !== bc) return ac < bc ? -1 : 1;
      const ae = (a.engagement_title || '').toLowerCase();
      const be = (b.engagement_title || '').toLowerCase();
      if (ae !== be) return ae < be ? -1 : 1;
    }
    return (a.date_of_expense || '') > (b.date_of_expense || '') ? -1 : 1;
  });
});

const filteredTotal = computed(() =>
  visibleExpenses.value.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
);

const displayRows = computed(() => {
  if (grouping.value === 'ungrouped') {
    return visibleExpenses.value.map((exp) => ({ kind: 'expense', exp }));
  }
  const rows = [];
  let lastKey = Symbol('init');
  let runningGroup = null;
  for (const exp of visibleExpenses.value) {
    let key, headerLabel;
    if (grouping.value === 'engagement') {
      key = (exp.engagement_title || '__none__') + '|' + (exp.client_name || '');
      headerLabel = `${exp.client_name || '(no client)'} — ${exp.engagement_title || '(no engagement)'}`;
    } else if (grouping.value === 'client') {
      key = exp.client_name || '__none__';
      headerLabel = exp.client_name || '(no client)';
    } else if (grouping.value === 'invoice') {
      key = exp.client_invoice_batch_reference || '__none__';
      headerLabel = exp.client_invoice_batch_reference
        ? `Invoice ${exp.client_invoice_batch_reference}`
        : '(not yet invoiced)';
    } else {
      key = '__all__';
      headerLabel = '';
    }
    if (key !== lastKey) {
      runningGroup = { kind: 'group', key, headerLabel, count: 0, total: 0 };
      rows.push(runningGroup);
      lastKey = key;
    }
    runningGroup.count += 1;
    runningGroup.total += parseFloat(exp.amount) || 0;
    rows.push({ kind: 'expense', exp, groupKey: key });
  }
  return rows;
});

function fullName(u) {
  if (!u) return '—';
  return (`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || `User #${u.id}`);
}

function formatDate(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(iso);
  return d.toLocaleDateString();
}

function formatAmount(a) {
  if (a === null || a === undefined) return '0.00';
  const n = typeof a === 'string' ? parseFloat(a) : a;
  return n.toFixed(2);
}

function formatTotal(n) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

async function loadExpenses() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    const data = await getExpenses(token, "?is_client_reimbursable=true&page_size=1000");
    expenses.value = Array.isArray(data) ? data : (data.results || []);
  } catch (e) {
    $toast.open({ message: "Failed to load client expenses", type: "error", duration: 3000 });
    expenses.value = [];
  }
  loading.value = false;
}

onMounted(loadExpenses);
</script>
